import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import LinkModal from './LinkModal';
import './RichTextEditor.css';

const MenuBar = ({ editor }) => {
  const [isLinkModalOpen, setIsLinkModalOpen] = React.useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = React.useState(false);
  const [linkModalData, setLinkModalData] = React.useState({ text: '', url: '' });
  const [imageModalData, setImageModalData] = React.useState({ text: '', url: '' });

  if (!editor) {
    return null;
  }

  const handleOpenLinkModal = () => {
    // Check if text is selected
    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;
    
    if (hasSelection) {
      // Get selected text
      const selectedText = editor.state.doc.textBetween(from, to, ' ');
      setLinkModalData({ text: selectedText, url: '' });
    } else {
      setLinkModalData({ text: '', url: '' });
    }
    
    setIsLinkModalOpen(true);
  };

  const handleOpenImageUrlModal = () => {
    setImageModalData({ text: '', url: '' });
    setIsImageModalOpen(true);
  };

  const handleInsertImageUrl = ({ text, url }) => {
    console.log('[IMAGE-INSERT] Inserting image URL as link:', { text, url });
    
    if (!url || !url.trim()) {
      console.warn('[IMAGE-INSERT] URL is empty, aborting');
      setIsImageModalOpen(false);
      return;
    }

    try {
      // Instead of inserting as image, insert as a link with image icon
      // This way it works even if the image can't be loaded
      const displayText = text && text.trim() ? text.trim() : url.trim();
      const imageIconText = `🖼️ ${displayText}`; // Cute image icon
      
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: imageIconText,
          marks: [{ type: 'link', attrs: { href: url.trim() } }]
        })
        .run();
      
      console.log('[IMAGE-INSERT] Image URL added as link with icon');
    } catch (error) {
      console.error('[IMAGE-INSERT] Error inserting image link:', error);
    }
    
    setIsImageModalOpen(false);
  };

  const handleInsertLink = ({ text, url }) => {
    console.log('[LINK-INSERT] Inserting link:', { text, url });
    
    if (!url || !url.trim()) {
      console.warn('[LINK-INSERT] URL is empty, aborting');
      setIsLinkModalOpen(false);
      return;
    }

    try {
      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;
      
      console.log('[LINK-INSERT] Selection info:', { hasSelection, from, to, hasText: !!text });
      
      if (hasSelection) {
        // Apply link to selected text
        console.log('[LINK-INSERT] Applying link to selected text');
        editor.chain().focus().setLink({ href: url }).run();
      } else if (text && text.trim()) {
        // Insert new text with link
        console.log('[LINK-INSERT] Inserting new link with text:', text);
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'text',
            text: text.trim(),
            marks: [{ type: 'link', attrs: { href: url.trim() } }]
          })
          .run();
      } else {
        console.warn('[LINK-INSERT] No text provided and no selection, cannot insert link');
      }
      
      console.log('[LINK-INSERT] Link inserted successfully');
    } catch (error) {
      console.error('[LINK-INSERT] Error inserting link:', error);
    }
    
    setIsLinkModalOpen(false);
  };

  return (
    <>
      <LinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onSubmit={handleInsertLink}
        initialText={linkModalData.text}
        initialUrl={linkModalData.url}
      />
      <LinkModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        onSubmit={handleInsertImageUrl}
        initialText={imageModalData.text}
        initialUrl={imageModalData.url}
      />
      <div className="menu-bar">
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
        type="button"
      >
        <strong>B</strong>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
        type="button"
      >
        <em>I</em>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'is-active' : ''}
        type="button"
      >
        <s>S</s>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleCode().run()}
        className={editor.isActive('code') ? 'is-active' : ''}
        type="button"
      >
        Code
      </button>
      
      <span className="divider">|</span>
      
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
        type="button"
      >
        H1
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
        type="button"
      >
        H2
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
        type="button"
      >
        H3
      </button>
      
      <span className="divider">|</span>
      
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
        type="button"
      >
        • List
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
        type="button"
      >
        1. List
      </button>
      
      <span className="divider">|</span>
      
      <button
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
        type="button"
      >
        ⬅️
      </button>
      <button
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
        type="button"
      >
        ↔️
      </button>
      <button
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
        type="button"
      >
        ➡️
      </button>
      
      <span className="divider">|</span>
      
      <button
        onClick={handleOpenLinkModal}
        className={editor.isActive('link') ? 'is-active' : ''}
        type="button"
        title="Insert or edit link"
      >
        🔗 Link
      </button>
      
      <button
        onClick={handleOpenImageUrlModal}
        type="button"
        title="Insert image from URL"
      >
        🔗 Image URL
      </button>
      
      <span className="divider">|</span>
      
      <button
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={editor.isActive('blockquote') ? 'is-active' : ''}
        type="button"
      >
        Quote
      </button>
      
      <button
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        type="button"
      >
        ―
      </button>
    </div>
    </>
  );
};

const RichTextEditor = React.forwardRef(({ value, onChange, placeholder, disabled = false }, ref) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Image,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      console.log('[RICH-TEXT-EDITOR] Content updated. HTML:', html);
      onChange(html);
    },
  });

  // Expose getSelectedText method through ref
  React.useImperativeHandle(ref, () => ({
    getSelectedText: () => {
      if (!editor) return '';
      const { from, to } = editor.state.selection;
      if (from === to) return ''; // No selection
      return editor.state.doc.textBetween(from, to, ' ');
    },
    replaceSelectedText: (newText) => {
      if (!editor) return;
      const { from, to } = editor.state.selection;
      if (from === to) {
        // No selection, insert at cursor position
        editor.commands.insertContent(newText);
      } else {
        // Replace selected text
        editor.commands.deleteRange({ from, to });
        editor.commands.insertContent(newText);
      }
    },
    hasSelection: () => {
      if (!editor) return false;
      const { from, to } = editor.state.selection;
      return from !== to;
    }
  }), [editor]);

  // Update editor content when value prop changes
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  // Update editable state when disabled prop changes
  React.useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  return (
    <div className={`rich-text-editor ${disabled ? 'disabled' : ''}`}>
      <MenuBar editor={editor} />
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
